import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { CompanyAddressDto, companyAddressValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { GreenText } from 'components'
import { LoadingButton } from '@mui/lab'
import { useEffect, useState } from 'react'
import * as controllers from 'controllers'
import { useFedExAddresses } from 'context'

interface AddressAddEditDialogProps {
  initialValues: CompanyAddressDto
  // isBrokerCompany is used to determine if the "Broker Company Address" address type switch should be enabled or disabled.
  isBrokerCompany: boolean
  // isImporterCompany is used to determine if the "Importer Company Address" address type switch should be enabled or disabled.
  isRegisteredImporter: boolean
  loading: boolean
  onClose: () => void
  onSave: (values: CompanyAddressDto) => void
  open: boolean
}

export default function AddressAddEditDialog({
  initialValues,
  isBrokerCompany,
  isRegisteredImporter,
  loading,
  onClose,
  onSave,
  open
}: AddressAddEditDialogProps) {
  const { countryCodes, stateProvinceCodes } = useFedExAddresses()

  const [isGettingBrokerCompanyAddress, setIsGettingBrokerCompanyAddress] = useState<boolean>(false)
  const [brokerCompanyAddressAlreadyExists, setBrokerCompanyAddressAlreadyExists] = useState<boolean>(false)
  const [isGettingRegisteredImporterAddress, setIsGettingRegisteredImporterAddress] = useState<boolean>(false)
  const [registeredImporterAddressAlreadyExists, setRegisteredImporterAddressAlreadyExists] = useState<boolean>(false)

  useEffect(() => {
    if (isBrokerCompany) {
      setIsGettingBrokerCompanyAddress(true)
      controllers
        .getBrokerCompanyAddress()
        .then(response => {
          if (response.value) {
            setBrokerCompanyAddressAlreadyExists(response.value.addressGuid !== initialValues.addressGuid)
          } else {
            setBrokerCompanyAddressAlreadyExists(false)
          }
        })
        .finally(() => {
          setIsGettingBrokerCompanyAddress(false)
        })
    }
  }, [isBrokerCompany, initialValues.addressGuid])

  useEffect(() => {
    if (isRegisteredImporter) {
      setIsGettingRegisteredImporterAddress(true)
      controllers
        .getRegisteredImporterAddress()
        .then(response => {
          if (response.value) {
            setRegisteredImporterAddressAlreadyExists(response.value.addressGuid !== initialValues.addressGuid)
          } else {
            setRegisteredImporterAddressAlreadyExists(false)
          }
        })
        .finally(() => {
          setIsGettingRegisteredImporterAddress(false)
        })
    }
  }, [isRegisteredImporter, initialValues.addressGuid])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={true}
        validationSchema={companyAddressValidationSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.addressGuid ? 'Edit' : 'Add'} Address</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      // There is a bug where the validation for addressNickname *will not* happen unless the user has actually clicked into
                      // the text field. Removing touched.addressNickname from error and helperText below fixes this bug.
                      //
                      // I do not know why, I even tried manually setting the error state in the SAVE button's onClick event.
                      //
                      // Maxwell Sommer 7/18/2024
                      error={Boolean(errors.addressNickname)}
                      helperText={errors.addressNickname}
                      label='Address Nickname'
                      name='addressNickname'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.addressNickname || ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                      label='Phone'
                      name='phone'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.phone}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                      label='Email'
                      name='email'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.email}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.address1 && errors.address1)}
                      helperText={touched.address1 && errors.address1}
                      label='Address 1'
                      name='address1'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.address1}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.address2 && errors.address2)}
                      helperText={touched.address2 && errors.address2}
                      label='Address 2'
                      name='address2'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.address2}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                      label='City'
                      name='city'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.city}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.county && errors.county)}
                      helperText={touched.county && errors.county}
                      label='County'
                      name='county'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.county ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={loading}
                      disableListWrap
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('stateProvince', value)

                        let country = undefined

                        if (value) {
                          let stateProvinceCode = stateProvinceCodes.filter(stateProvinceCode => stateProvinceCode.state === value)[0]
                          country = countryCodes.filter(countryCode => countryCode.code === stateProvinceCode.countryCode)[0]?.countryTerritory ?? undefined
                        }

                        setFieldValue('country', country)
                      }}
                      options={stateProvinceCodes.map(({ state }) => state)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.stateProvince && errors.stateProvince)}
                          helperText={touched.stateProvince && errors.stateProvince}
                          label='State / Province'
                        />
                      )}
                      value={values.stateProvince ?? null}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.postalCode && errors.postalCode)}
                      helperText={touched.postalCode && errors.postalCode}
                      label='Postal Code'
                      name='postalCode'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.postalCode}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField disabled error={Boolean(errors.country)} helperText={errors.country} label='Country' value={values.country || ''} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isActive}
                          disabled={loading || isSubmitting}
                          name='isActive'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isActive}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Active</Typography>

                          <Typography variant='body2' color='#808080'>
                            Makes address active.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isBillingAddress}
                          disabled={loading || isSubmitting}
                          name='isBillingAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isBillingAddress: "isBillingAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isBillingAddress', e.target.checked)
                          }}
                          value={values.isBillingAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Billing Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Company’s billing address.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isBrokerCompanyAddress}
                          disabled={loading || isSubmitting || !isBrokerCompany || isGettingBrokerCompanyAddress || brokerCompanyAddressAlreadyExists}
                          name='isBrokerCompanyAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isBrokerCompanyAddress: "isBrokerCompanyAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isBrokerCompanyAddress', e.target.checked)
                          }}
                          value={values.isBrokerCompanyAddress}
                        />
                      }
                      label={
                        <Grid container>
                          <Grid item>
                            <Typography>Broker Company Address</Typography>

                            <Typography variant='body2' color='#808080'>
                              Broker company’s address.
                            </Typography>
                          </Grid>

                          <Grid item>{isGettingBrokerCompanyAddress && <CircularProgress size={25} />}</Grid>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isClusterConversionCompanyAddress}
                          disabled={loading || isSubmitting}
                          name='isClusterConversionCompanyAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isClusterConversionCompanyAddress: "isClusterConversionCompanyAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isClusterConversionCompanyAddress', e.target.checked)
                          }}
                          value={values.isClusterConversionCompanyAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Cluster Conversion Company Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Cluster conversion company's address.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isExporterCompanyAddress}
                          disabled={loading || isSubmitting}
                          name='isExporterCompanyAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isExporterCompanyAddress: "isExporterCompanyAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isExporterCompanyAddress', e.target.checked)
                          }}
                          value={values.isExporterCompanyAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Exporter Company Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            The email address associated with this Exporter Company Address receives any “Missing CAN Ownership” notification emails. Used for
                            Title Applications.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isDamageNotificationAddress}
                          disabled={loading || isSubmitting}
                          name='isDamageNotificationAddress'
                          onBlur={handleBlur}
                          onChange={e => {
                            setFieldValue('isDamageNotificationAddress', e.target.checked)
                          }}
                          value={values.isDamageNotificationAddress}
                        />
                      }
                      label={
                        <Grid container>
                          <Grid item>
                            <Typography>Damage Notification Address</Typography>

                            <Typography variant='body2' color='#808080'>
                              The email address associated with this Buyer Company or Exporter Company will receive any damage notification emails generated by
                              VTS2. Note that if a vehicle has a Buyer Company, that Buyer Company will receive the damage notification email and not the
                              Exporter Company. If there is no Buyer Company, the Exporter Company will receive the Damage Notification email.
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                  </Grid>

                  {/* <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isBuyerCompanyAddress}
                          disabled={loading || isSubmitting}
                          name='isBuyerCompanyAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isBuyerCompanyAddress: "isBuyerCompanyAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isBuyerCompanyAddress', e.target.checked)
                          }}
                          value={values.isBuyerCompanyAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Buyer Company Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Used for Title Applications.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>
                  */}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isImportLocationAddress}
                          disabled={loading || isSubmitting}
                          name='isImportLocationAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isImportLocationAddress: "isImportLocationAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isImportLocationAddress', e.target.checked)
                          }}
                          value={values.isImportLocationAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Import Location</Typography>

                          <Typography variant='body2' color='#808080'>
                            Allows this address to appear in the Import Location dropdown. Associated with the company where the vehicle is being imported into
                            and may be different than the Registered Importer's address. Used for Conformity process to indicate where the vehicles were
                            converted and stored.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isInboundTransporterAddress}
                          disabled={loading || isSubmitting}
                          name='isInboundTransporterAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isInboundTransporterAddress: "isInboundTransporterAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isInboundTransporterAddress', e.target.checked)
                          }}
                          value={values.isInboundTransporterAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Inbound Transporter Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            The email address associated with this Inbound Transporter Company address will appear in the Inbound Transporter Company Email
                            dropdown when adding or editing an Inbound Trip.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isInspectionCompanyAddress}
                          disabled={loading || isSubmitting}
                          name='isInspectionCompanyAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isInspectionCompanyAddress: "isInspectionCompanyAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isInspectionCompanyAddress', e.target.checked)
                          }}
                          value={values.isInspectionCompanyAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Inspection Company Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Allows this address to appear in the Inspection Company Address dropdown. Used for Ohio Title Application Packet.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isInsuranceUnderwriterCompanyAddress}
                          disabled={loading || isSubmitting}
                          name='isInsuranceUnderwriterCompanyAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isInsuranceUnderwriterCompanyAddress: "isInsuranceUnderwriterCompanyAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isInsuranceUnderwriterCompanyAddress', e.target.checked)
                          }}
                          value={values.isInsuranceUnderwriterCompanyAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Insurance Underwriter Company Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Insurance Underwriter Company's address.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isMichiganSOSBranchAddress}
                          disabled={loading || isSubmitting}
                          name='isMichiganSOSBranchAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isMichiganSOSBranchAddress: "isMichiganSOSBranchAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          onChange={e => {
                            setFieldValue('isMichiganSOSBranchAddress', e.target.checked)
                          }}
                          value={values.isMichiganSOSBranchAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Michigan SoS Branch Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Allows this address to appear in the Michigan SoS Branch Address dropdown used in Michigan Titles
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isOutboundTransporterAddress}
                          disabled={loading || isSubmitting}
                          name='isOutboundTransporterAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isOutboundTransporterAddress: "isOutboundTransporterAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isOutboundTransporterAddress', e.target.checked)
                          }}
                          value={values.isOutboundTransporterAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Outbound Transporter Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            The email address associated with this Outbound Transporter Company address will appear in the Outbound Transporter Company Email
                            dropdown when adding or editing an Outbound Trip.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isPhysicalAddress}
                          disabled={loading || isSubmitting}
                          name='isPhysicalAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isPhysicalAddress: "isPhysicalAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isPhysicalAddress', e.target.checked)
                          }}
                          value={values.isPhysicalAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Physical Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Company’s physical address.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isRecallLocationAddress}
                          disabled={loading || isSubmitting}
                          name='isRecallLocationAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isRecallLocationAddress: "isRecallLocationAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isRecallLocationAddress', e.target.checked)
                          }}
                          value={values.isRecallLocationAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Recall Location</Typography>

                          <Typography variant='body2' color='#808080'>
                            Allows this address to appear in the Recall Location dropdown. Used for Recall Campaigns and selecting where “Recall to be Repaired
                            in US” will be performed/remedied.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isRegisteredImporterAddress}
                          disabled={
                            loading || isSubmitting || !isRegisteredImporter || isGettingRegisteredImporterAddress || registeredImporterAddressAlreadyExists
                          }
                          name='isRegisteredImporterAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isRegisteredImporterAddress: "isRegisteredImporterAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isRegisteredImporterAddress', e.target.checked)
                          }}
                          value={values.isRegisteredImporterAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Registered Importer Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            For this Registered Importer, this address will appear on system generated documents (i.e., Pro Forma).
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isShippingAddress}
                          disabled={loading || isSubmitting}
                          name='isShippingAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isShippingAddress: "isShippingAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isShippingAddress', e.target.checked)
                          }}
                          value={values.isShippingAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Shipping Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Company's shipping address.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isSpecialMailerAddress}
                          disabled={loading || isSubmitting}
                          name='isSpecialMailerAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isSpecialMailerAddress: "isSpecialMailerAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isSpecialMailerAddress', e.target.checked)
                          }}
                          value={values.isSpecialMailerAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Special Mailer Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            For this Title Destination Company, this address will appear in the Special Mailer Address dropdown for system generated documents
                            (i.e., RD-108).
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isTitleDestinationAddress}
                          disabled={loading || isSubmitting}
                          name='isTitleDestinationAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isTitleDestinationAddress: "isTitleDestinationAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isTitleDestinationAddress', e.target.checked)
                          }}
                          value={values.isTitleDestinationAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Title Destination Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            For this Title Destination Company, this address will appear in the Title Destination Address dropdown
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isVehicleDestinationAddress}
                          disabled={loading || isSubmitting}
                          name='isVehicleDestinationAddress'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isVehicleDestinationAddress: "isVehicleDestinationAddress must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isVehicleDestinationAddress', e.target.checked)
                          }}
                          value={values.isVehicleDestinationAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Vehicle Destination Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            For this Vehicle Destination Company, this address will appear in the Vehicle Destination Address dropdown.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Address Nickname:**
                    
Required free-text field.

**Phone Number:**

Numeric-only free-text field.

**Email:**

Free-text field for entering an email address.

**Address 1:**

Free-text field for the primary address line.

**Address 2:**

Free-text field for the secondary address line.

**City:**

Free-text field for the city name.

**County:**

Optional free-text field for the county name.

**State/Province:**

Free-text field for the state or province.

**Postal Code:**

Free-text field for the postal or ZIP code.

**Country:**

Free-text field for the country name.

**Active Switch:**

Makes address active

**Billing Address Switch:**

Indicates the company's billing address.

**Broker Company Address Switch:**

Indicates the Broker Company's address.

- There can only be one Broker Company Address in the system.

- The Broker Company Address switch is disabled if this company is not a Broker 
or if a Broker Company Address already exists.

- To enable this address for the current company, the Broker Company Address 
must be toggled off for the other company.

**Exporter Company Address Switch:**

The email address associated with the Exporter Company Address will receive any "Missing CAN Ownership" notification emails. These notifications are used for Title Applications. 

**Damage Notification Address:**

The email address associated with this Buyer Company or Exporter Company will 
receive any damage notification emails generated by VTS2. Note that if a vehicle 
has a Buyer Company, that Buyer Company will receive the damage notification 
email and not the Exporter Company. If there is no Buyer Company, the Exporter 
Company will receive the Damage Notification email.

**Import Location Switch:**

Allows the address to appear in the Import Location dropdown.

- This is associated with the company where the vehicle is being imported and 
may differ from the Registered Importer's address.

- Used in the Conformity process to indicate where vehicles are converted and 
stored.

**Inbound Transporter Address Switch:**

The email address associated with this Inbound Transporter Company address 
will appear in the Inbound Transporter Company Email dropdown when adding or 
editing an Inbound Trip.

**Inspection Company Address Switch:**

Enables this address to appear in the Inspection Company Address dropdown.

Used in the Ohio Title Application Packet process.

**Insurance Underwriter Company Address Switch:**

Indicates the Insurance Underwriter Company's address.

**Outbound Transporter Address Switch:**

The email address associated with this Outbound Transporter Company address 
will appear in the Outbound Transporter Company Email dropdown when adding 
or editing an Outbound Trip.

**Physical Address Switch:**

Indicates the company's physical address.

**Recall Location Switch:**

Allows the address to appear in the Recall Location dropdown.

- Used for Recall Campaigns to select where the “Recall to be Repaired in US” will 
be performed or remedied.

**Registered Importer Address Switch:**

For the Registered Importer, this address will appear on system-generated 
documents (e.g., Pro Forma).

- Only one Registered Importer Company and Registered Importer Address can 
exist in the system at a time.

- This switch is disabled if a Registered Importer Address is already assigned.

- To enable this address for the current company, the existing Registered Importer 
Address must be toggled off.

**Shipping Address Switch:**

Indicates the company's shipping address.

**Special Mailer Address Switch:**

For the Title Destination Company, this address will appear in the Special Mailer 
Address dropdown for system-generated documents (e.g., RD-108).

**Michigan SoS Branch Address Switch:**

Allows this address to appear under the Michigan SoS Branch Address drop down used in Michigan Titles.

**Title Destination Address Switch:**

For the Title Destination Company, this address will appear in the Title Destination 
Address dropdown.

**Vehicle Destination Address Switch:**

For the Vehicle Destination Company, this address will appear in the Vehicle 
Destination Address dropdown.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={loading || isSubmitting} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
